import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Menu, Moon, Sun, LogOut, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Logo } from "@/components/layout/Logo";
import { NavList, SidebarFooter } from "@/components/layout/Sidebar";
import { useAuthStore } from "@/lib/auth/store";
import { initials, titleCase } from "@/lib/utils";

function useDarkMode() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("osf-theme", dark ? "dark" : "light");
  }, [dark]);

  return { dark, toggle: () => setDark((d) => !d) };
}

export function Topbar() {
  const { dark, toggle } = useDarkMode();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate("/login");
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center border-b border-border bg-background/85 px-4 backdrop-blur-md sm:px-6">
      {/* Below lg (phones, tablets) the sidebar becomes a drawer behind ☰. */}
      <div className="flex items-center gap-2 lg:hidden">
        <MobileNav />
        <Logo variant="icon" className="h-8" />
      </div>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
        <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        <div className="hidden h-6 w-px bg-border sm:block" />

        <div className="flex items-center gap-2.5">
          <div className="hidden text-right leading-tight sm:block">
            <p className="text-sm font-medium">{user?.fullName ?? "—"}</p>
            <Badge variant="outline" className="text-[10px]">
              {titleCase(user?.role)}
            </Badge>
          </div>
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">
            {initials(user?.fullName)}
          </div>
        </div>

        <ConfirmDialog
          trigger={
            <Button variant="ghost" size="icon" aria-label="Log out">
              <LogOut className="h-4 w-4" />
            </Button>
          }
          title="Log out?"
          description="You'll need your username and password to sign back in."
          confirmLabel="Log out"
          onConfirm={handleLogout}
        />
      </div>
    </header>
  );
}

/** The sidebar's links as a left drawer, for phones and tablets. */
function MobileNav() {
  const [open, setOpen] = useState(false);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </Button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-border bg-card shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left">
          <DialogPrimitive.Title className="sr-only">Menu</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">Navigate the admin portal</DialogPrimitive.Description>
          <div className="flex h-16 items-center justify-between border-b border-border px-5">
            <Logo variant="wordmark" />
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close menu">
                <X className="h-4 w-4" />
              </Button>
            </DialogPrimitive.Close>
          </div>
          <NavList layoutId="drawer-active" onNavigate={() => setOpen(false)} />
          <SidebarFooter />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
